import { AuiIf, ThreadPrimitive, useThread, useThreadRuntime } from "@assistant-ui/react";
import { HelpCircle, MessageSquarePlus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import { AGENT_NAME } from "./agentName";
import { AlertBanner } from "./AlertBanner";
import { AskTigerLogo } from "./AskTigerLogo";
import { AssistantMessage } from "./AssistantMessage";
import { CategoryView } from "./CategoryView";
import { Composer } from "./Composer";
import { useGenUIInput } from "./genui/GenUIInputContext";
import { HelpOverlay } from "./HelpOverlay";
import { HistoryTab } from "./HistoryTab";
import { UserMessage } from "./UserMessage";
import { WelcomeScreen } from "./WelcomeScreen";
import { sidePanelStyles as s } from "./SidePanel.styles";
import type { SidePanelProps } from "./SidePanel.types";
import type { HistoryEntry } from "./HistoryTab.types";
import type { CategoryId } from "./CategoryView.types";

type PanelTab = "chats" | "history";

export function SidePanel({
  onClose,
  userName,
  version,
  alerts = [],
  onDismissAlert,
  historyEntries = [],
  onSelectHistoryEntry,
  onRenameHistoryEntry,
  onDeleteHistoryEntry,
  historyLoading,
  historyError,
  onNewChat,
  welcomeCards,
  welcomeCardsLoading,
}: SidePanelProps) {
  const threadRuntime = useThreadRuntime();
  const { dismissInput } = useGenUIInput();
  const [panelTab, setPanelTab] = useState<PanelTab>("chats");
  const [showHelp, setShowHelp] = useState(false);
  const [activeCategory, setActiveCategory] = useState<CategoryId | null>(null);

  // Leave the category view — and dismiss any active input component — as soon as a
  // message lands on the thread (e.g. the user answered by typing in the composer
  // instead of interacting with the input; option-select / manual-dismiss already
  // cleared it, so this is a no-op for those).
  const messageCount = useThread((t) => t.messages.length);
  const prevMessageCount = useRef(messageCount);
  useEffect(() => {
    if (messageCount > prevMessageCount.current) {
      setActiveCategory(null);
      dismissInput();
    }
    prevMessageCount.current = messageCount;
  }, [messageCount, dismissInput]);

  const handleNewChat = () => {
    // A fresh session id is minted by AgentProvider remounting in response to onNewChat, which
    // fully replaces the runtime — no need to also switch assistant-ui's own internal thread.
    onNewChat?.();
    setPanelTab("chats");
    setShowHelp(false);
    setActiveCategory(null);
  };

  const handleSelectHistoryEntry = (entry: HistoryEntry) => {
    setPanelTab("chats");
    onSelectHistoryEntry?.(entry);
  };

  const handleSelectPrompt = (prompt: string) => {
    setActiveCategory(null);
    threadRuntime.append(prompt);
  };

  return (
    <div className={s.root}>
      <div className={s.header}>
        <div className={s.headerBrand}>
          <AskTigerLogo className={s.headerAvatar} />
          <span className={s.headerTitle}>{AGENT_NAME}</span>
          {version && <span className={s.headerVersion}>v{version}</span>}
        </div>
        <div className={s.headerActions}>
          <button
            onClick={() => setShowHelp(true)}
            aria-label="Help"
            title="Help"
            className={s.headerBtn}
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              aria-label={`Close ${AGENT_NAME}`}
              title="Close"
              className={s.headerBtn}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <Tabs
        value={showHelp ? "help" : panelTab}
        onValueChange={(value) => {
          setPanelTab(value as PanelTab);
          setShowHelp(false);
        }}
        className="shrink-0 gap-0"
      >
        <div className={s.tabBar}>
          <TabsList variant="line" className="h-auto gap-0 p-0">
            <TabsTrigger value="chats" className={`${s.tabTrigger} ${s.chatTabTrigger}`}>
              Chat
            </TabsTrigger>
            <TabsTrigger value="history" className={s.tabTrigger}>
              History
            </TabsTrigger>
          </TabsList>
          <div className="flex-1" />
          <button
            onClick={handleNewChat}
            aria-label="New chat"
            title="New chat"
            className={s.newChatBtn}
          >
            <MessageSquarePlus className="h-4 w-4" />
          </button>
        </div>
      </Tabs>

      <div className={s.content}>
        {showHelp ? (
          <HelpOverlay version={version} />
        ) : panelTab === "history" ? (
          <HistoryTab
            entries={historyEntries}
            onSelect={handleSelectHistoryEntry}
            onRename={onRenameHistoryEntry}
            onDelete={onDeleteHistoryEntry}
            loading={historyLoading}
            error={historyError}
          />
        ) : activeCategory ? (
          <>
            <CategoryView
              category={activeCategory}
              onBack={() => setActiveCategory(null)}
              onSelectPrompt={handleSelectPrompt}
            />
            <Composer />
          </>
        ) : (
          <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col">
            <ThreadPrimitive.Viewport className="flex flex-1 flex-col overflow-y-auto">
              {alerts.length > 0 && (
                <div className={s.alertList}>
                  {alerts.map((alert) => (
                    <AlertBanner
                      key={alert.id}
                      {...alert}
                      onDismiss={() => onDismissAlert?.(alert.id)}
                    />
                  ))}
                </div>
              )}

              <ThreadPrimitive.Empty>
                <WelcomeScreen
                  userName={userName}
                  onSelectCategory={setActiveCategory}
                  agentCards={welcomeCards}
                  cardsLoading={welcomeCardsLoading}
                />
              </ThreadPrimitive.Empty>

              <div className={s.messages}>
                <AuiIf condition={(st) => !st.thread.isEmpty}>
                  <p className={s.disclaimer}>
                    <span className={s.disclaimerRule} aria-hidden />
                    AI-generated responses. Always verify.
                    <span className={s.disclaimerRule} aria-hidden />
                  </p>
                </AuiIf>
                <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
              </div>

              <ThreadPrimitive.ViewportFooter className={s.threadFooter}>
                <Composer />
              </ThreadPrimitive.ViewportFooter>
            </ThreadPrimitive.Viewport>
          </ThreadPrimitive.Root>
        )}
      </div>
    </div>
  );
}
